import { ImagePlus, Loader2, Trash2 } from "lucide-react";
import { useRef } from "react";
import { Button } from "../../../../components/ui/common/button";
import { errorView } from "../../../../helpers/ErrorHandler";

const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

interface UserAvatarUploadProps {
  /** Picture to preview: a persisted signed URL (edit) or a local object URL (register). */
  imageUrl?: string | null;
  /** Letter(s) shown in the circle while there is no picture. */
  initial: string;
  isBusy?: boolean;
  onUpload: (file: File) => void;
  onRemove: () => void;
}

/** Profile-picture card: circle preview (initial fallback) + hint + Upload/Replace and Remove. Validates type and the 2 MB limit it advertises. */
const UserAvatarUpload = ({ imageUrl, initial, isBusy = false, onUpload, onRemove }: UserAvatarUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSelect = (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      errorView("Picture must be a JPG, PNG or WebP image");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      errorView("Picture must be 2 MB or smaller");
      return;
    }
    onUpload(file);
  };

  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-5 tw-rounded-xl tw-border tw-border-border tw-bg-muted/30 tw-p-6">
      <div className="tw-flex tw-h-28 tw-w-28 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-border tw-border-border tw-bg-muted">
        {imageUrl ? (
          <img src={imageUrl} alt="User profile" className="tw-h-full tw-w-full tw-object-cover" />
        ) : (
          <span className="tw-text-3xl tw-font-semibold tw-text-foreground">{initial}</span>
        )}
      </div>
      <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1.5">
        <p className="tw-text-base tw-font-medium tw-text-foreground">
          {imageUrl ? "Profile picture" : "Add a profile picture"}
        </p>
        <p className="tw-text-sm tw-text-muted-foreground">JPG, PNG or WebP. Maximum 2 MB.</p>
        <div className="tw-mt-1 tw-flex tw-gap-2">
          <Button type="button" variant="outline" disabled={isBusy} onClick={() => inputRef.current?.click()}>
            {isBusy ? <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin" /> : <ImagePlus className="tw-h-4 tw-w-4" />}
            {imageUrl ? "Replace picture" : "Upload picture"}
          </Button>
          {imageUrl ? (
            <Button type="button" variant="outline" size="icon" title="Remove picture" disabled={isBusy} onClick={onRemove}>
              <Trash2 className="tw-h-4 tw-w-4" />
            </Button>
          ) : null}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(", ")}
        className="tw-hidden"
        data-testid="user-avatar-input"
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) handleSelect(file);
          e.target.value = "";
        }}
      />
    </div>
  );
};

export default UserAvatarUpload;
